import React, {useState, useEffect} from 'react';

import "./GatesPerspectiveTable.css";
import {Table} from "../../../common/table/Table";
import {StandardButton} from "../../../common/button/standard-button/StandardButton";
import {ConfirmDeleteModal} from "../../../common/confirmation-modal/ConfimDeleteModal";
import {LeftLabelSelect} from "../../../common/input/left-label-select/LeftLabelSelect";
import {
    createGatesLibrary,
    deleteGatesLibrary,
    retrieveAllGatesLibraries, retrieveGatesLibrary, updateGatesLibrary
} from "../../../../services/GatesLibraryService";
import {uploadFile, viewFilesForGatesLibrary} from "../../../../services/FileService";
import {SelectableTable} from "../../../common/table/SelectableTable";

export const GatesPerspectiveTable = ({context, setContext, navigateTo, setSpinnerText, showToaster}) => {

    const [filesForPackage, setFilesForPackage] = useState([]);
    const [gatesLibraries, setGatesLibraries] = useState([]);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [noResultsMessage, setNoResultsMessage] = useState(null);
    const [libraryName, setLibraryName] = useState("");
    const [extRefToBeDeleted, setExtRefToBeDeleted] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState(null);
    const [libraryVisibility, setLibraryVisibility] = useState("");

    const roles = context.roles;
    let isAdmin;
    if (null !== roles) {
        isAdmin = roles.includes("qi-admin");
    }

    useEffect(() => {
        const fetchData = async () => {
            if (context.captureNewLibrary) {
                if (context.addFilesToLibrary) {
                    setNoResultsMessage("No Files Selected");
                    setLibraryVisibility(context.libraryVisibility);
                    setLibraryName(context.libraryName)
                    setFilesToUpload(context.filesToUploadList);
                }
            } else {
                if (context.addFilesToLibrary) {
                    await fetchFilesForGatesLibrary()
                    setLibraryName(context.selectedLibrary.name);
                    setLibraryVisibility(({
                        label: context.selectedLibrary.visibility,
                        value: context.selectedLibrary.visibility
                    }));
                } else {
                    setLibraryName("");
                    await fetchAllLibraries()
                }
            }
        }
        fetchData();
    }, [context]);

    const fetchAllLibraries = async () => {
        setSpinnerText("Retrieving All Libraries...");
        const [data, error] = await retrieveAllGatesLibraries(context.username);
        if (data) {
            if (data.responseStatus === "Success") {
                const visibleLibrariesForUser = [];
                for (const library of data.gatesReportLibraryVOList) {
                    switch (library.visibility) {
                        case "Public":
                            visibleLibrariesForUser.push(library);
                            break;
                        case "Private":
                            if (context.username === library.creator) {
                                visibleLibrariesForUser.push(library);
                            }
                            break;
                        case "Admin":
                            if (isAdmin) {
                                visibleLibrariesForUser.push(library);
                            }
                            break;
                    }
                }
                setGatesLibraries(visibleLibrariesForUser);
                showToaster(data);
            } else {
                if (data.responseMessage !== "There was an error when trying to view the Gates Report Libraries." +
                    " No Gates Report Libraries found in the database.") {
                    showToaster(data);
                } else {
                    setGatesLibraries([]);
                }
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    const fetchFilesForGatesLibrary = async () => {
        setSpinnerText("Retrieving File List...");
        let libraryExtRef = context.selectedLibrary.externalReference;
        const [data, error] = await viewFilesForGatesLibrary(context.username, libraryExtRef);
        if (data) {
            if (data.responseStatus === "Success") {
                setFilesForPackage(data.filesToView);
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    const handleNewLibraryClick = () => {
        setContext((prevState) => ({
            ...prevState,
            captureNewLibrary: true,
            addFilesToLibrary: true,
            libraryName: null,
            filesToUploadList: [],
            libraryVisibility: null,
            selectedLibrary: null,
        }));
    }

    const handleSaveNewLibraryClick = async () => {
        setSpinnerText("Creating Library...");
        const gatesReportLibraryVO = {
            name: libraryName,
            creator: context.username,
            modifiedBy: context.username,
            visibility: libraryVisibility.value,
        };
        const [data, error] = await createGatesLibrary(context.username, gatesReportLibraryVO);
        if (data) {
            if (data.responseStatus === "Success") {
                await updateSelectedGatesFiles(filesToUpload, data.gatesReportLibraryVO);
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setContext((prevState) => ({
            ...prevState,
            captureNewLibrary: false,
            addFilesToLibrary: false,
        }));
        navigateTo("gatesPerspectiveTable");
        setSpinnerText(null);
    }

    const updateSelectedGatesFiles = async (selectedFiles, createdLibrary) => {
        for (const file of selectedFiles) {
            setSpinnerText("Uploading file...");
            const gatesFileVOToCreate = {
                name: file.name,
                creatorExternalReference: context.loggedInUser.externalReference,
                modifiedByExternalReference: context.loggedInUser.externalReference,
                contextObjectReferenceVO: {
                    externalReference: createdLibrary.externalReference,
                    contextId: 30,
                },
                category: "gates",
                data: file.data,
                visibility: createdLibrary.visibility,
            }
            const [data, error] = await uploadFile(context.loggedInUser, gatesFileVOToCreate);
            if (data) {
                showToaster(data);
            } else {
                showToaster({
                    responseStatus: "Error",
                    responseMessage: error.message,
                });
            }
            setSpinnerText(null);
        }
    }

    const handleLibraryRowClick = async (library) => {
        const [data, error] = await retrieveGatesLibrary(context.username, library.externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                const savedLibrary = data.gatesReportLibraryVOList[0];
                setContext((prevState) => ({
                    ...prevState,
                    selectedLibrary: savedLibrary,
                    addFilesToLibrary: true,
                    captureNewLibrary: false,
                }));
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
    }

    const handleFileRowClick = async (file) => {
        setContext((prevState) => ({
            ...prevState,
            libraryName: libraryName,
            selectedFile: file,
        }));
        navigateTo("gatesPerspectiveCapture");
    }

    const navigateBackToViewLibraries = () => {
        setSpinnerText("loading...");
        setFilesForPackage([]);
        setContext((prevState) => ({
            ...prevState,
            filesForPackage: [],
            libraryName: null,
            filesToUploadList: [],
            libraryVisibility: null,
            selectedLibrary: null,
            addFilesToLibrary: false,
            captureNewLibrary: false,
            selectedFile: null,
        }));
        navigateTo("gatesPerspectiveTable");
        setSpinnerText(null);
    };

    const handleUpdateLibraryClick = async () => {
        setSpinnerText("Updating Library...");
        setFilesForPackage([]);
        const gatesReportLibraryVO = {
            externalReference: context.selectedLibrary.externalReference,
            name: libraryName,
            creator: context.username,
            modifiedBy: context.username,
            visibility: libraryVisibility.value,
        };
        const [data, error] = await updateGatesLibrary(context.username, gatesReportLibraryVO);
        if (data) {
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
        setContext((prevState) => ({
            ...prevState,
            captureNewLibrary: false,
            addFilesToLibrary: false,
            selectedLibrary: null,
        }));
        navigateTo("gatesPerspectiveTable");
    }

    const handleAddFileToLibraryClick = async () => {
        setContext((prevState) => ({
            ...prevState,
            addFilesToLibrary: false,
            libraryName: libraryName,
            filesToUploadList: filesToUpload,
            libraryVisibility: libraryVisibility,
            addClick: true,
            selectedFile: null,
        }));
        navigateTo("gatesPerspectiveCapture");
    }

    const handleDeleteLibraryClick = async (externalReference) => {
        setSpinnerText("Deleting Library...");
        const [data, error] = await deleteGatesLibrary(context.username, externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                setShowModal(false);
                navigateBackToViewLibraries()
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    const handleDeleteModalShow = (extRef, title) => {
        setShowModal(true);
        setExtRefToBeDeleted(extRef);
        setModalTitle(title);
    }

    return (
        <div className={"gates-perspective-screen-container"}>
            {(context.captureNewLibrary || !context.captureNewLibrary && context.addFilesToLibrary) &&
            <div className={"gates-perspective-name-container"}>
                <label>{"Grant Library Name: "}</label>
                <input type="text" value={libraryName}
                       onChange={({target}) => {
                           setLibraryName(target.value)
                       }}/>
            </div>}

            <div className={"gates-perspective-header-container"}>
                {(!context.captureNewLibrary && !context.addFilesToLibrary) &&
                <h2 className="report-header">Gates Report</h2>}
                {(context.captureNewLibrary || context.addFilesToLibrary) &&
                <div className="gates-perspective-visibility-capture-container">
                    <div className="gates-perspective-visibility-capture">
                        <LeftLabelSelect labelText="Visibility:" options={(isAdmin) ?
                            [{value: "Public", label: "Public"},
                                {value: "Admin", label: "Admin"},
                                {value: "Private", label: "Private"},] :
                            [{value: "Public", label: "Public"},
                                {value: "Private", label: "Private"},]} value={libraryVisibility}
                                         onChange={(selectedVisibility) => {
                                             setLibraryVisibility(selectedVisibility);
                                         }}/>
                    </div>
                </div>}

                {(context.captureNewLibrary || !context.captureNewLibrary && context.addFilesToLibrary) &&
                <label>Files in Grant Library:</label>}
            </div>

            {(context.captureNewLibrary) &&
            <div className="gates-perspective-table-container">
                <Table tableData={filesToUpload}
                       buttonText={(context.captureNewLibrary || context.addFilesToLibrary) ? null : "New Library"}
                       onButtonClick={handleNewLibraryClick}
                       searchEnabled={true}
                       noResultsMessage={noResultsMessage}
                       onRowClick={(file) => handleFileRowClick(file)}
                       hiddenColumns={["data", "contextObjectReferenceVO", "externalReference"]}/>
            </div>}

            {(!context.captureNewLibrary) &&
            <div className="gates-perspective-table-container">
                <SelectableTable tableData={(context.addFilesToLibrary) ? filesForPackage : gatesLibraries}
                                 buttonText={(context.captureNewLibrary || context.addFilesToLibrary) ? null : "New Library"}
                                 onButtonClick={handleNewLibraryClick}
                                 searchEnabled={true}
                                 noResultsMessage={noResultsMessage}
                                 onRowClick={(context.addFilesToLibrary) ? (file) => handleFileRowClick(file) : handleLibraryRowClick}
                                 hiddenColumns={(context.addFilesToLibrary) ?
                                     ["data", "contextObjectReferenceVO", "externalReference", "fileDataUrl", "sharepointFileName",
                                         "category", "modifiedByExternalReference", "creatorExternalReference",
                                         "creator", "modifiedBy", "modifiedDate", "visibility"]
                                     : ["data", "contextObjectReferenceVO", "externalReference", "isDeleted", "created", "lastModified"]}/>
            </div>}

            <div className={"gates-perspective-button-container"}>
                {(context.captureNewLibrary && context.addFilesToLibrary) &&
                <StandardButton text={"Save"} onClick={handleSaveNewLibraryClick}
                                disabled={!libraryName || !libraryVisibility}/>}

                {(!context.captureNewLibrary && context.addFilesToLibrary) &&
                <StandardButton text={"Update"} onClick={handleUpdateLibraryClick} disabled={!libraryName}/>}

                {(context.captureNewLibrary && context.addFilesToLibrary || context.selectedLibrary) &&
                <StandardButton text={"Add File"} onClick={handleAddFileToLibraryClick}/>}

                {(context.addFilesToLibrary && !context.captureNewLibrary) &&
                <StandardButton text={"Delete Library"} onClick={() => {
                    handleDeleteModalShow(context.selectedLibrary.externalReference, context.selectedLibrary.name);
                }}/>}

                {showModal &&
                <ConfirmDeleteModal showModal={showModal}
                                    setShowModal={setShowModal}
                                    titleToDelete={modalTitle}
                                    onDeleteClick={async () => {
                                        await handleDeleteLibraryClick(extRefToBeDeleted)
                                    }}/>}

                {(context.captureNewLibrary || context.addFilesToLibrary) &&
                <StandardButton text={"Back"} onClick={navigateBackToViewLibraries}/>}
            </div>
        </div>
    );
}
